// ============================================================================
// /build-room — the command center (items 701 and 702, part of 700; TCSD-0412).
//
// A superuser-only page that shows the board live — Needs you, Projects,
// Running now, Feedback, and the morning message — and, since Run 2, acts on it:
// every press is one call to the bridge, signed by the key's owner, and only
// the key's owner may press (decision 11). Anyone else who can open it reads it.
//
// The board is read and written through the build-room-bridge function, which
// holds the key; feedback is read from app_feedback through the app's own
// client and written through the shared save (admin/feedback-save.ts).
//
// Access: the menu entry is superuser-only (nav-config.tsx), the page refuses
// anyone else the way /admin/feedback does, and the bridge itself answers 403
// to anyone whose is_superuser() is false — the page guard is the courtesy,
// the bridge is the control.
// ============================================================================
import { isSuperuser } from '../../shared/role-gates';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../shared/auth';
import { EmptyState, PageHeader } from '../../shared/components';
import { refreshReaders, useReader } from '../../shared/save-path';
import {
  readBridge,
  readDemo,
  readFeedbackLinks,
  readOpenFeedback,
  readWhoami,
  writeBridge,
  type DemosView,
  type FeedbackLinksView,
  type FeedbackRow,
  type ItemsView,
  type Read,
  type RunsView,
  type Whoami,
} from './api';
import { CommandCenterView } from './view';
import { AskBox } from './ask-box';
import type { FeedbackKind } from './actions';
import { saveFeedback } from '../admin/feedback-save';
import { describeError } from '../../shared/errors';

export default function BuildRoomPage() {
  const { profile } = useAuth();
  const superuser = isSuperuser(profile);

  const [items, setItems] = useState<Read<ItemsView> | null>(null);
  const [runs, setRuns] = useState<Read<RunsView> | null>(null);
  const [feedback, setFeedback] = useState<Read<FeedbackRow[]> | null>(null);
  const [who, setWho] = useState<Read<Whoami> | null>(null);
  const [links, setLinks] = useState<Read<FeedbackLinksView> | null>(null);
  const [demos, setDemos] = useState<Read<DemosView> | null>(null);
  const [reading, setReading] = useState(false);

  // A refresh keeps what is on screen until the new answer arrives (TCSD-0039):
  // nothing is reset to null, so the tabs never flash back to a skeleton.
  const loadBoard = useCallback(async () => {
    setReading(true);
    const [i, r, d] = await Promise.all([readBridge('items'), readBridge('runs'), readBridge('demos')]);
    setItems(i);
    setRuns(r);
    setDemos(d);
    setReading(false);
  }, []);
  const loadFeedback = useCallback(async () => {
    const f = await readOpenFeedback();
    setFeedback(f);
    // Whether each open report's item finished another way (decision 7).
    if (f.ok) setLinks(await readFeedbackLinks(f.data.map((r) => r.feedback_id)));
  }, []);

  useEffect(() => {
    if (!superuser) return;
    void loadBoard();
    void loadFeedback();
    void readWhoami().then(setWho);
  }, [superuser, loadBoard, loadFeedback]);

  // A report closed here: the shared save first, then any board item he asked to
  // close with the same words. A failure says which half did not happen.
  const onFeedbackSave = useCallback(
    async (row: FeedbackRow, kind: FeedbackKind, text: string, closeItems: number[]): Promise<string | null> => {
      const payload =
        kind === 'complete'
          ? ({ status: 'completed', resolution: text } as const)
          : kind === 'wont'
            ? ({ status: 'wont_fix', resolution: text } as const)
            : ({ status: 'duplicate', duplicate_of: text } as const);
      const { error } = await saveFeedback(row.feedback_id, payload);
      if (error) return describeError(error, "Couldn't close this report.").message;
      refreshReaders(['app_feedback']);
      const failed: string[] = [];
      for (const n of closeItems) {
        const r = await writeBridge({ action: 'close', item: n, feedback_id: row.feedback_id, words: text });
        if (!r.ok) failed.push(r.message);
      }
      void loadBoard();
      void loadFeedback();
      return failed.length
        ? `${row.feedback_id} is closed and the reporter is told, but its board item was not closed: ${failed.join(' ')}`
        : null;
    },
    [loadBoard, loadFeedback],
  );

  // A feedback status changed anywhere in the app re-reads the Feedback tab.
  useReader(['app_feedback'], loadFeedback);
  // A board action confirmed in the ask box (item #737) re-reads the board.
  useReader(['build_board'], loadBoard);

  // Same refusal, same words, as the other superuser-only page (/admin/feedback).
  if (profile && !superuser) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <PageHeader title="Command center" />
        <EmptyState
          title="Superusers only"
          body="The command center shows the build board and is gated to superusers."
        />
      </div>
    );
  }

  // Drawn as the approved design: the command center is its own panel inside the
  // app, with no page title or banner above it. Refresh and the read time sit in
  // its bar.
  return (
    <div className="mx-auto max-w-6xl p-3 md:p-5">
      <CommandCenterView
        items={items}
        runs={runs}
        feedback={feedback}
        who={who}
        feedbackLinks={links}
        onWrite={writeBridge}
        onFeedbackSave={onFeedbackSave}
        readAt={items?.ok ? items.data.read_at : null}
        reading={reading}
        renderAsk={(needs) => <AskBox needs={needs} />}
        demos={demos}
        loadDemo={readDemo}
        onRefresh={() => {
          void loadBoard();
          void loadFeedback();
        }}
      />
    </div>
  );
}
